import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '@/components/Header';
import { Buildings, HeartStraight, Brain, ForkKnife, TrendUp, Package, CheckCircle, Clock, XCircle, Lightning } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  pending: { label: 'Pending Approval', color: '#F59E0B', bg: '#FEF3C7', icon: Clock },
  accepted: { label: 'Accepted', color: '#3B82F6', bg: '#DBEAFE', icon: CheckCircle },
  confirmed: { label: 'Confirmed', color: '#3B82F6', bg: '#DBEAFE', icon: CheckCircle },
  active: { label: 'Active', color: '#34D399', bg: '#D1FAE5', icon: Lightning },
  completed: { label: 'Completed', color: '#6B7280', bg: '#F3F4F6', icon: CheckCircle },
  rejected: { label: 'Rejected', color: '#EF4444', bg: '#FEE2E2', icon: XCircle },
};

const Dashboard = ({ user }) => {
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [requests, setRequests] = useState([]);
  const [cateringOrders, setCateringOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [renewing, setRenewing] = useState(false);

  const handleRenew = async () => {
    setRenewing(true);
    try {
      await axios.post(`${BACKEND_URL}/api/subscriptions/renew`, {}, { withCredentials: true });
      toast.success('Subscription renewed! Your next cycle starts right after the current one ends.');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to renew subscription');
    } finally {
      setRenewing(false);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const [subRes, reqRes, catRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/subscriptions/my-subscription`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${BACKEND_URL}/api/subscription/my-requests`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/corporate-catering/my-orders`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setSubscription(subRes.data);
      setRequests(reqRes.data || []);
      setCateringOrders(catRes.data || []);
    } catch (error) {
      // Silent — non-critical fetch
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.role === 'admin') {
      navigate('/admin', { replace: true });
      return;
    }
    fetchData();
  }, [user, navigate, fetchData]);

  const quickLinks = [
    {
      title: 'Corporate Orders',
      description: 'Bulk ordering for events',
      icon: Buildings,
      link: '/corporate',
      color: 'text-[#1A4331]',
      bg: 'bg-[#1A4331]/10',
    },
    {
      title: 'Fitness Plans',
      description: 'Subscribe to meal plans',
      icon: HeartStraight,
      link: '/fitness',
      color: 'text-[#C05C3D]',
      bg: 'bg-[#C05C3D]/10',
    },
    {
      title: 'Browse Meals',
      description: 'Explore our menu',
      icon: ForkKnife,
      link: '/meals',
      color: 'text-[#FBBF24]',
      bg: 'bg-[#FBBF24]/10',
    },
  ];

  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <Header user={user} />

      <main className="px-6 md:px-12 lg:px-24 py-12" data-testid="dashboard-main">
        {/* Welcome Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <h1 className="text-4xl sm:text-5xl lg:text-5xl font-semibold tracking-tight text-[#1C1917] mb-4 font-heading" data-testid="welcome-heading">
            Welcome back, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="text-base leading-relaxed text-stone-600">
            Manage your fitness journey and corporate orders all in one place.
          </p>
        </motion.div>

        {/* Subscription Status */}
        {subscription && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white border border-stone-200 shadow-sm rounded-2xl p-6 md:p-8 mb-8"
            data-testid="subscription-status"
          >
            {(() => {
              const daysLeft = Math.ceil((new Date(subscription.end_date) - new Date()) / (1000 * 60 * 60 * 24));
              const nearingEnd = daysLeft <= 7;
              return (
                <>
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Package size={24} weight="duotone" className="text-[#C05C3D]" />
                        <h3 className="text-xl sm:text-2xl font-medium text-[#1C1917] font-heading">Active Subscription</h3>
                      </div>
                      <p className="text-lg text-stone-700 mb-1 font-medium">{subscription.plan_details?.name}</p>
                      <p className="text-stone-600">Status: <span className="text-[#34D399] font-medium capitalize">{subscription.status}</span></p>
                      <p className="text-sm text-stone-500 mt-2">
                        {subscription.plan_details?.meals_per_day} meals per day • Valid until {new Date(subscription.end_date).toLocaleDateString()}
                      </p>
                      {nearingEnd && daysLeft >= 0 && (
                        <div className="mt-3 inline-flex items-center gap-2 bg-[#FEF3C7] text-[#92400E] px-3 py-1.5 rounded-full text-xs font-semibold" data-testid="renewal-warning">
                          <Clock size={14} weight="fill" />
                          {daysLeft === 0 ? 'Ends today' : daysLeft === 1 ? 'Ends tomorrow' : `${daysLeft} days left`}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {nearingEnd ? (
                        <>
                          <button
                            onClick={handleRenew}
                            disabled={renewing}
                            className="bg-gradient-to-br from-[#1A4331] to-[#2A5A44] text-white px-5 py-2.5 rounded-full font-semibold text-sm hover:shadow-lg hover:shadow-[#1A4331]/25 hover:-translate-y-0.5 transition-all disabled:opacity-60"
                            data-testid="renew-btn"
                          >
                            {renewing ? 'Renewing…' : 'Renew'}
                          </button>
                          <button
                            onClick={() => navigate('/plans?renewal=1')}
                            className="border border-stone-300 text-stone-800 hover:bg-stone-100 px-5 py-2.5 rounded-full font-semibold text-sm transition-all"
                            data-testid="change-plan-btn"
                          >
                            Change Plan
                          </button>
                        </>
                      ) : (
                        <Link
                          to="/fitness"
                          className="border border-stone-300 text-stone-900 hover:bg-stone-100 px-4 py-2 rounded-full font-medium transition-all duration-200 text-sm"
                          data-testid="manage-subscription-link"
                        >
                          Manage
                        </Link>
                      )}
                    </div>
                  </div>
                </>
              );
            })()}
          </motion.div>
        )}

        {/* Corporate Catering Orders */}
        {cateringOrders.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-12"
            data-testid="catering-orders"
          >
            <h2 className="text-2xl sm:text-3xl font-medium tracking-tight text-[#1C1917] mb-6 font-heading">Corporate Catering Orders</h2>
            <div className="space-y-4">
              {cateringOrders.map((order) => {
                const status = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
                const StatusIcon = status.icon;
                return (
                  <div key={order.request_id} className="bg-white border border-stone-200 shadow-sm rounded-2xl p-6" data-testid={`catering-order-${order.request_id}`}>
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-xs font-bold uppercase tracking-[0.2em] text-stone-500">Request ID</span>
                          <span className="font-bold text-[#1A4331]">{order.request_id}</span>
                          <span className="text-xs bg-[#C05C3D]/10 text-[#C05C3D] px-2 py-0.5 rounded-full font-medium">Catering</span>
                        </div>
                        <h3 className="text-lg font-medium text-[#1C1917] font-heading">{order.event_name}</h3>
                        <p className="text-sm text-stone-600 mt-1">
                          {order.total_meals} meals • Event: {order.event_date} at {order.event_time}
                        </p>
                        <p className="text-sm font-bold text-[#1A4331] mt-1">Total: ₹{order.total?.toLocaleString()}</p>
                      </div>
                      <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium" style={{ backgroundColor: status.bg, color: status.color }}>
                        <StatusIcon size={16} weight="fill" />
                        {status.label}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Subscription Requests */}
        {requests.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="mb-12"
            data-testid="subscription-requests"
          >
            <h2 className="text-2xl sm:text-3xl font-medium tracking-tight text-[#1C1917] mb-6 font-heading">My Subscription Requests</h2>
            <div className="space-y-4">
              {requests.map((req) => {
                const status = STATUS_CONFIG[req.status] || STATUS_CONFIG.pending;
                const StatusIcon = status.icon;
                const planName = (() => {
                  if (req.subscriptions && req.subscriptions.length > 0) {
                    return req.subscriptions.map(s => {
                      if (s.type === 'post-workout') return s.plan === 'weight-loss' ? 'Weight Loss' : 'Muscle Gain';
                      if (s.type === 'lunch') return 'Lunch';
                      if (s.type === 'dinner') return 'Dinner';
                      return s.type;
                    }).join(' + ');
                  }
                  return req.plan_type === 'weight-loss' ? 'Weight Loss Plan' : req.plan_type === 'muscle-gain' ? 'Muscle Gain Plan' : 'Subscription';
                })();
                return (
                  <div key={req.request_id} className="bg-white border border-stone-200 shadow-sm rounded-2xl p-6" data-testid={`request-${req.request_id}`}>
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <p className="text-xs font-bold uppercase tracking-[0.2em] text-stone-500">Request ID</p>
                          <span className="font-bold text-[#1A4331]">{req.request_id}</span>
                        </div>
                        <h3 className="text-lg font-medium text-[#1C1917] font-heading">{planName}</h3>
                        <p className="text-sm text-stone-600 mt-1">
                          Start Date: {req.start_date} • Requested: {new Date(req.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium" style={{ backgroundColor: status.bg, color: status.color }}>
                        <StatusIcon size={16} weight="fill" />
                        {status.label}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Quick Links */}
        <div className="mb-12">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight text-[#1C1917] mb-6 font-heading">
            Quick Actions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {quickLinks.map((item, index) => (
              <motion.div
                key={item.link}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
              >
                <Link
                  to={item.link}
                  className="block bg-white border border-stone-200 shadow-sm hover:-translate-y-1 hover:shadow-lg transition-all duration-300 rounded-2xl p-6"
                  data-testid={`quick-link-${item.title.toLowerCase().replace(' ', '-')}`}
                >
                  <div className={`${item.bg} ${item.color} w-12 h-12 rounded-xl flex items-center justify-center mb-4`}>
                    <item.icon size={24} weight="duotone" />
                  </div>
                  <h3 className="text-lg font-medium text-[#1C1917] mb-1">{item.title}</h3>
                  <p className="text-sm text-stone-600">{item.description}</p>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
